import axios from 'axios';
import SessionService from './SessionService';
import { config } from '../config';
import appCache from './AppCache';

const API_URL = config.API_BASE_URL;

export interface Tag {
  id: number;
  name: string;
  description: string;
  sectorId: number;
  color: string;
}

export interface CreateTagRequestDTO {
  name: string;
  description: string;
  sectorId: number;
  color: string;
}

export interface UpdateTagRequestDTO {
  name: string;
  description: string;
  sectorId: number;
}

interface ApiResponse<T> {
  message: string;
  code: string;
  data: T;
}

export const createTag = async (tagData: CreateTagRequestDTO) => {
  try {
    const response = await axios.post(`${API_URL}/tags`, tagData, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to create tag: ${error}`);
  }
};

export const updateTag = async (id: number, data: UpdateTagRequestDTO) => {
  try {
    const response = await axios.put(`${API_URL}/tags/${id}`, data, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to update tag: ${error}`);
  }
};

export const getTag = async (id: number): Promise<Tag> => {
  try {
    const response = await axios.get(`${API_URL}/tags/${id}`, {
      headers: {
        'Accept': '*/*',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to get tag with id ${id}: ${error}`);
  }
};

export const getTags = async (sectorId: number, forceRefresh = false): Promise<ApiResponse<Tag[]>> => {

  if (!sectorId) {
    throw new Error('Sector ID is not available in session');
  }

  const cacheKey = `tags:${sectorId}`;
  if (!forceRefresh) {
    const cached = appCache.get<ApiResponse<Tag[]>>(cacheKey);
    if (cached) return cached;
  }

  try {
    const response = await axios.get(`${API_URL}/tags?sectorId=${sectorId}`, { // Passando o sectorId na URL
      headers: {
        'Accept': '*/*',
        'Authorization': `Bearer ${SessionService.getToken()}`
      },
    });
    appCache.set(cacheKey, response.data, 5 * 60 * 1000);
    return response.data;
  } catch (error) {
    throw new Error('Failed to get tags: ' + error);
  }
};

export const deleteTag = async (id: number) => {
  try {
    const response = await axios.delete(`${API_URL}/tags/${id}`, {
      headers: {
        'Authorization': `Bearer ${SessionService.getToken()}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(`Failed to delete tag with id ${id}: ${error}`);
  }
};